import type { SchemaColumn, SchemaField } from '@cli-types/core-engine/schema.js'
import { buildSortLabelExpr } from '@next-adapter/generators/sortable-header/build-sort-label-expr.js'
import { buildSortableHeaderButtonJsx } from '@next-adapter/generators/sortable-header/build-sortable-header-button-jsx.js'
import { buildColumnSize } from '@next-adapter/generators/table-column-size/build-column-size.js'
import { renderFirstColumnDef } from '@next-adapter/templates/renderers/generators/columns/column-defs/generate-first-column-def.js'
import { buildColumnMeta } from './build-column-meta.js'
import { fieldIsRelationship } from './field-is-relationship.js'
import { fieldIsSelect } from './field-is-select.js'
import { generateAuthorshipValue } from './generate-authorship-value.js'
import { generateColumnDef } from './generate-column-def.js'
import { generateImageCellMedia } from './generate-image-cell-media.js'
import { generateRelationshipLabel } from './generate-relationship-label.js'
import { generateSelectLabel } from './generate-select-label.js'
import { getAuthorshipUserAccessor } from './get-authorship-user-accessor.js'
import { getBooleanColumnLabels } from './get-boolean-column-labels.js'
import { isSortableColumn } from './is-sortable-column.js'

export function generateFirstColumnDef(
  column: SchemaColumn,
  fields: SchemaField[],
  options: { selectable?: boolean } = {}
): string {
  if (options.selectable === false) {
    return generateColumnDef(column, fields)
  }

  const sortable = isSortableColumn(column)
  const header = sortable
    ? {
        sortable: true as const,
        sortLabelExpr: buildSortLabelExpr(),
        sortableHeaderButtonJsx: buildSortableHeaderButtonJsx(column.header, {
          className: 'flex-1'
        })
      }
    : { sortable: false as const, columnHeader: column.header }

  let cell:
    | { kind: 'authorship'; generateAuthorshipValue: string }
    | { kind: 'image'; generateImageCellMedia: string }
    | { kind: 'select-badge'; columnAccessorKey: string; generateSelectLabel: string }
    | { kind: 'relationship-badge'; columnAccessorKey: string; generateRelationshipLabel: string }
    | { kind: 'badge-value'; columnAccessorKey: string }
    | { kind: 'date'; columnAccessorKey: string }
    | { kind: 'email'; columnAccessorKey: string }
    | { kind: 'currency'; columnAccessorKey: string }
    | { kind: 'number'; columnAccessorKey: string }
    | { kind: 'boolean'; columnAccessorKey: string; trueLabel: string; falseLabel: string }
    | { kind: 'custom'; columnComponent: string }
    | { kind: 'select'; columnAccessorKey: string; generateSelectLabel: string }
    | { kind: 'relationship'; columnAccessorKey: string; generateRelationshipLabel: string }
    | { kind: 'plain'; columnAccessorKey: string }
    | { kind: 'link'; columnAccessorKey: string }
  const authorshipUserAccessor = getAuthorshipUserAccessor(column.accessorKey)
  const isSelectColumn = fieldIsSelect(column, fields)
  const isRelationshipColumn = fieldIsRelationship(column, fields)

  if (authorshipUserAccessor) {
    cell = {
      kind: 'authorship',
      generateAuthorshipValue: generateAuthorshipValue(authorshipUserAccessor)
    }
  } else
    switch (column.type) {
      case 'image':
      case 'avatar':
        cell = {
          kind: 'image',
          generateImageCellMedia: generateImageCellMedia(
            column,
            fields,
            `row.original.${column.accessorKey}`,
            { selectable: true }
          )
        }
        break
      case 'badge':
        cell = isSelectColumn
          ? {
              kind: 'select-badge',
              columnAccessorKey: column.accessorKey,
              generateSelectLabel: generateSelectLabel('value')
            }
          : isRelationshipColumn
            ? {
                kind: 'relationship-badge',
                columnAccessorKey: column.accessorKey,
                generateRelationshipLabel: generateRelationshipLabel('value')
              }
            : { kind: 'badge-value', columnAccessorKey: column.accessorKey }
        break
      case 'date':
        cell = { kind: 'date', columnAccessorKey: column.accessorKey }
        break
      case 'email':
        cell = { kind: 'email', columnAccessorKey: column.accessorKey }
        break
      case 'number':
        if (column.format === 'currency') {
          cell = { kind: 'currency', columnAccessorKey: column.accessorKey }
        } else {
          cell = { kind: 'number', columnAccessorKey: column.accessorKey }
        }
        break
      case 'boolean':
        {
          const { trueLabel, falseLabel } = getBooleanColumnLabels(column, fields)
          cell = {
            kind: 'boolean',
            columnAccessorKey: column.accessorKey,
            trueLabel,
            falseLabel
          }
        }
        break
      case 'custom':
        if (column.component) {
          cell = { kind: 'custom', columnComponent: column.component }
        } else {
          cell = isSelectColumn
            ? {
                kind: 'select',
                columnAccessorKey: column.accessorKey,
                generateSelectLabel: generateSelectLabel('value')
              }
            : isRelationshipColumn
              ? {
                  kind: 'relationship',
                  columnAccessorKey: column.accessorKey,
                  generateRelationshipLabel: generateRelationshipLabel('value')
                }
              : { kind: 'plain', columnAccessorKey: column.accessorKey }
        }
        break
      case 'link':
        cell = { kind: 'link', columnAccessorKey: column.accessorKey }
        break
      default:
        cell = isSelectColumn
          ? {
              kind: 'select',
              columnAccessorKey: column.accessorKey,
              generateSelectLabel: generateSelectLabel('value')
            }
          : isRelationshipColumn
            ? {
                kind: 'relationship',
                columnAccessorKey: column.accessorKey,
                generateRelationshipLabel: generateRelationshipLabel('value')
              }
            : { kind: 'plain', columnAccessorKey: column.accessorKey }
        break
    }

  return renderFirstColumnDef({
    columnAccessorKey: column.accessorKey,
    columnSize: buildColumnSize(column.size),
    meta: buildColumnMeta(column),
    sortable,
    header,
    cell
  })
}
